import dayjs from "dayjs";
import { TranslationStore } from "../../i18n/translation-store";
import { ServiceInterface } from "../service.interface";
import {
  reaction,
  action,
  computed,
  IReactionDisposer,
  observable,
} from "mobx";
import { PreferenceService } from "../preference";
import { ApiService } from "../api";
import { Translation } from "../../i18n/translation";
import { DefaultLogger } from "../../logger";
import { createContext } from "react";
import { DeviceService } from "../device";
import { TransTokens } from "../../i18n/trans-tokens";

export interface SupportedLanguage {
  value: string;
  label: Translation;
}

export const I18nServiceContext = createContext<I18nService>(null as any);

export class I18nService implements ServiceInterface {
  public context = I18nServiceContext;

  constructor(
    private apiService: ApiService,
    private preferenceService: PreferenceService,
    private deviceService: DeviceService,
    public readonly store = TranslationStore
  ) {}

  private translations = this.preferenceService.STORED_TRANSLATIONS;
  private lastRefresh?: Date;

  private subscriptions: IReactionDisposer[] = [];

  public async serviceDidBecomeForeground() {
    // Wait until dependent preferences are ready
    await Promise.all([
      this.preferenceService.PREF_LANGUAGE.ready$,
      this.preferenceService.STORED_TRANSLATIONS.ready$,
    ]);

    // Refresh stores on activelanguage swich
    this.subscriptions.push(
      reaction(
        () => this.activeLanguage,
        () => this.refreshStores(),
        {
          fireImmediately: true,
        }
      )
    );

    this.refreshIfStale();
  }

  public async serviceDidBecomeBackground() {
    for (const dispose of this.subscriptions) dispose();
  }

  public getLanguage(iso: string) {
    return this.translations.value?.find((t) => t.id === iso);
  }

  @computed
  public get supportedLanguages(): SupportedLanguage[] {
    return (
      (this.translations.value || [])
        // When a language is not enabled, don't show it as a supported language
        .filter(({ enabled }) => !!enabled)
        .map(({ id, name }) => {
          return {
            value: id,
            label: TransTokens.getTranslation(name),
          };
        })
    );
  }

  public isLanguageSupported(iso: string) {
    return !!this.getLanguage(iso);
  }

  @computed
  public get deviceDefaultLanguage() {
    if (this.deviceService.getPreferredLanguages) {
      const preferred = this.deviceService.getPreferredLanguages() || [];
      const supported = this.supportedLanguages;

      for (const { full, short } of preferred) {
        const v =
          supported.find(({ value }) => value === full) ||
          supported.find(({ value }) => value.split("-")[0] === short);

        if (v) return v.value;
      }
    }

    const activeLanguage = this.deviceService.getDeviceLanguage();
    return activeLanguage.short;
  }

  /**
   * The preferred game language of the user
   */
  @computed
  public get preferredLanguage() {
    if (this.preferenceService.PREF_LANGUAGE.value)
      return this.preferenceService.PREF_LANGUAGE.value;

    return this.deviceDefaultLanguage;
  }

  @computed
  public get savedPreferredLanguage() {
    return this.preferenceService.PREF_LANGUAGE.value;
  }

  @observable
  public priorityLanguage?: string;

  @action
  public setPriorityLanguage(lang?: string) {
    this.priorityLanguage = lang;
  }

  @computed
  public get activeLanguage() {
    return this.priorityLanguage || this.preferredLanguage;
  }

  @action
  public setPreferredLanguage(lang: string) {
    this.preferenceService.PREF_LANGUAGE.setValue(lang);
  }

  private refreshStores() {
    let iso = this.activeLanguage;
    if (!this.isLanguageSupported(iso)) iso = "en";

    const language = this.getLanguage(iso);
    if (!language) TranslationStore.resetTranslations();
    else TranslationStore.setTranslations(language.translations || {});

    const newLanguage = language?.id || "en";
    TranslationStore.setActiveLanguage(newLanguage);

    try {
      // Safely set locale, because this might crash the app if locale is not
      // available at all (used to be the case because metero tree shaking may
      // remove some locales)
      dayjs.locale(newLanguage);
    } catch (e) {
      DefaultLogger.captureException(e);
    }
  }

  private refreshIfStale = async () => {
    // Refresh translations after one hour of inactivity
    const inactivityDurationMs = 60 * 60 * 1000;

    if (
      !this.lastRefresh ||
      +new Date() - +this.lastRefresh > inactivityDurationMs
    ) {
      try {
        const { data } = await this.apiService.clientPublic.getTranslations();
        this.translations.setValue(data);
        this.refreshStores();
        this.lastRefresh = new Date();
      } catch (e) {}
    }
  };
}
